import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { quoteApi } from '../services/quoteApi';
import type { QuoteFormData } from '../types/crm.types';

export const quoteKeys = {
  all: ['quotes'] as const,
  lists: () => [...quoteKeys.all, 'list'] as const,
  list: (filters: Record<string, any> = {}) => [...quoteKeys.lists(), filters] as const,
  details: () => [...quoteKeys.all, 'detail'] as const,
  detail: (id: string, params?: Record<string, any>) => {
    const key = params && Object.keys(params).length > 0
      ? [...quoteKeys.details(), id, params]
      : [...quoteKeys.details(), id];
    return key as readonly unknown[];
  },
  pdfs: (id: string) => [...quoteKeys.all, 'pdfs', id] as const,
  preview: (id: string) => [...quoteKeys.all, 'preview', id] as const,
};

/**
 * Hook to fetch quotes with optional filters and pagination
 */
export function useQuotes(params?: {
  page?: number;
  per_page?: number;
  search?: string;
  quote_status?: string;
  deleted?: 'only' | 'with';
  /** #480/#481 — committed-quote guard + sibling filters */
  inventory_item_id?: string;
  committed?: boolean;
  active?: boolean;
}) {
  return useQuery({
    queryKey: quoteKeys.list(params),
    queryFn: () => quoteApi.getQuotes(params),
  });
}

/**
 * Hook to fetch single quote
 */
export function useQuote(id: string, params?: { include_deleted?: boolean }) {
  const keyParams = params && Object.keys(params).length > 0 ? params : undefined;

  return useQuery({
    queryKey: quoteKeys.detail(id, keyParams),
    queryFn: () => quoteApi.getQuote(id, params),
    enabled: !!id,
  });
}

/**
 * Hook to fetch PDFs for a quote
 */
export function useQuotePdfs(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: quoteKeys.pdfs(id),
    queryFn: () => quoteApi.getQuotePdfs(id),
    enabled: options?.enabled ?? !!id,
  });
}

/**
 * Hook to create quote
 */
export function useCreateQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: QuoteFormData) => quoteApi.createQuote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
  });
}

/**
 * Hook to update quote
 */
export function useUpdateQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<QuoteFormData> }) =>
      quoteApi.updateQuote(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
  });
}

/**
 * Hook to delete quote
 */
export function useDeleteQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quoteApi.deleteQuote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
  });
}

/**
 * Hook to send quote
 */
export function useSendQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { id: string; recipient?: 'buyer' | 'co_buyer' }) =>
      quoteApi.sendQuote(payload),
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(payload.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
  });
}

/**
 * Hook to accept quote
 */
export function useAcceptQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quoteApi.acceptQuote(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
  });
}

/**
 * Hook to decline quote
 */
export function useDeclineQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      quoteApi.declineQuote(id, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
  });
}

/**
 * Hook to preview quote HTML (NEW)
 */
export function useQuotePreview() {
  return useMutation({
    mutationFn: (quoteId: string) => quoteApi.previewQuote(quoteId),
  });
}

/**
 * Hook to approve quote and generate PDF (NEW)
 */
type ApproveQuotePayload =
  | string
  | {
      quoteId: string;
      recipient?: 'buyer' | 'co_buyer';
      documentTitle?: string;
    };

export function useApproveQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ApproveQuotePayload) => {
      if (typeof payload === 'string') {
        return quoteApi.approveQuote(payload);
      }
      return quoteApi.approveQuote(payload.quoteId, payload.recipient, payload.documentTitle);
    },
    onSuccess: (_, payload) => {
      const quoteId = typeof payload === 'string' ? payload : payload.quoteId;
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(quoteId) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
      queryClient.invalidateQueries({ queryKey: quoteKeys.pdfs(quoteId) });
    },
  });
}

/**
 * Hook to reject quote generation (NEW)
 */
export function useRejectQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quoteId, reason }: { quoteId: string; reason?: string }) =>
      quoteApi.rejectQuote(quoteId, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(variables.quoteId) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
  });
}

/**
 * Hook to fetch snapshot vs live inventory drift — powers the staleness
 * banner on the quote detail page (#436).
 */
export function useQuoteInventoryDrift(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...quoteKeys.detail(id), 'inventory-drift'],
    queryFn: () => quoteApi.getInventoryDrift(id),
    enabled: options?.enabled ?? !!id,
  });
}

/**
 * Hook to supersede a quote with a fresh revision built from live
 * inventory (#436). The old quote moves to the `superseded` status.
 */
export function useSupersedeQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (quoteId: string) => quoteApi.supersedeQuote(quoteId),
    onSuccess: (_, quoteId) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(quoteId) });
      queryClient.invalidateQueries({ queryKey: quoteKeys.lists() });
    },
  });
}
